import { useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import { useNavigate } from '@tanstack/react-router'
import 'maplibre-gl/dist/maplibre-gl.css'
import MapUnavailable from './MapUnavailable'
import type { Event } from '@/lib/types'
import { isGeocoded, isWebglAvailable } from '@/lib/helpers'
import { catOf } from '@/lib/types'
import { circleBounds, circlePolygon } from '@/lib/circle'

const MAP_STYLE =
  'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json'

const RADIUS_SOURCE_ID = 'radius-source'
const RADIUS_FILL_LAYER_ID = 'radius-fill'
const RADIUS_LINE_LAYER_ID = 'radius-line'

/**
 * MapLibre map plotting geocoded events as teardrop pins, coloured by event
 * category. Hovering a pin reports the active event upward; clicking navigates
 * to the event detail page.
 */
export default function EventsMap({
  events,
  active,
  onActive,
  radius,
}: {
  events: Array<Event>
  active?: number | null
  onActive?: (id: number | null) => void
  radius?: { lat: number; lng: number; km: number }
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markersRef = useRef<Record<number, maplibregl.Marker>>({})
  const navigate = useNavigate()
  const [failed, setFailed] = useState(() => !isWebglAvailable())

  // Init once.
  useEffect(() => {
    if (failed || !containerRef.current) return
    let map: maplibregl.Map
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: MAP_STYLE,
        center: [2.6, 47.0],
        zoom: 4.6,
        attributionControl: false,
      })
    } catch {
      setFailed(true)
      return
    }
    map.on('error', (e) => {
      if (e.error.message.includes('WebGL')) setFailed(true)
    })
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      'top-right',
    )
    map.addControl(new maplibregl.AttributionControl({ compact: true }))
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [failed])

  // (Re)build markers.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    Object.values(markersRef.current).forEach((m) => m.remove())
    markersRef.current = {}

    const bounds = new maplibregl.LngLatBounds()
    const geocoded = events.filter((e) => isGeocoded(e.location))
    geocoded.forEach((e) => {
      const cat = catOf(e.type.type)
      const el = document.createElement('div')
      el.className = 'oumma-pin'
      el.style.setProperty('--pin', cat.color)
      el.innerHTML = '<span class="oumma-pin-star"></span>'
      el.addEventListener('mouseenter', () => onActive?.(e.id))
      el.addEventListener('mouseleave', () => onActive?.(null))
      el.addEventListener('click', (ev) => {
        ev.stopPropagation()
        navigate({ to: '/events/$id', params: { id: String(e.id) } })
      })
      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([e.location.longitude, e.location.latitude])
        .addTo(map)
      markersRef.current[e.id] = marker
      bounds.extend([e.location.longitude, e.location.latitude])
    })

    if (geocoded.length && !bounds.isEmpty()) {
      try {
        map.fitBounds(bounds, { padding: 70, maxZoom: 12, duration: 600 })
      } catch {
        /* ignore */
      }
    }
  }, [events])

  // Draw / update the search-radius circle and fit the map to it.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const apply = () => {
      const existing = map.getSource<maplibregl.GeoJSONSource>(RADIUS_SOURCE_ID)
      if (!radius) {
        if (map.getLayer(RADIUS_LINE_LAYER_ID))
          map.removeLayer(RADIUS_LINE_LAYER_ID)
        if (map.getLayer(RADIUS_FILL_LAYER_ID))
          map.removeLayer(RADIUS_FILL_LAYER_ID)
        if (existing) map.removeSource(RADIUS_SOURCE_ID)
        return
      }
      const feature = circlePolygon(radius.lat, radius.lng, radius.km)
      if (existing) {
        existing.setData(feature)
      } else {
        map.addSource(RADIUS_SOURCE_ID, { type: 'geojson', data: feature })
        map.addLayer({
          id: RADIUS_FILL_LAYER_ID,
          type: 'fill',
          source: RADIUS_SOURCE_ID,
          paint: {
            'fill-color': '#0f766e',
            'fill-opacity': 0.08,
          },
        })
        map.addLayer({
          id: RADIUS_LINE_LAYER_ID,
          type: 'line',
          source: RADIUS_SOURCE_ID,
          paint: {
            'line-color': '#0f766e',
            'line-width': 2,
            'line-dasharray': [2, 2],
          },
        })
      }
      try {
        map.fitBounds(circleBounds(radius.lat, radius.lng, radius.km), {
          padding: 70,
          maxZoom: 13,
          duration: 600,
        })
      } catch {
        /* ignore */
      }
    }
    if (map.isStyleLoaded()) apply()
    else map.once('load', apply)
  }, [radius?.lat, radius?.lng, radius?.km])

  // Highlight active marker.
  useEffect(() => {
    Object.entries(markersRef.current).forEach(([id, m]) => {
      m.getElement().classList.toggle('is-active', Number(id) === active)
    })
  }, [active])

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 320,
        background: 'var(--surface-sunken)',
      }}
    >
      {failed ? (
        <MapUnavailable />
      ) : (
        <div
          ref={containerRef}
          style={{ position: 'absolute', inset: 0, borderRadius: 'inherit' }}
        />
      )}
    </div>
  )
}
